// =====================================================
// One-Pager de Licitación — Word (.doc) + PDF (via print).
// Documento resumido de ~5-6 páginas, visual, compartible.
// Sin dependencias externas.
// =====================================================

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const REC_META = {
  presentarse:                 { label: 'Presentarse',                 color: '#0f766e', bg: '#d1fae5', bar: '#0f766e' },
  presentarse_con_condiciones: { label: 'Presentarse con condiciones', color: '#a16207', bg: '#fef3c7', bar: '#f59e0b' },
  no_presentarse:              { label: 'No presentarse',              color: '#b91c1c', bg: '#fee2e2', bar: '#dc2626' },
};

const NA = 'Dato no disponible en el análisis';

function safe(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return s.length ? s : '';
}
function or(v, fallback = NA) {
  const s = safe(v);
  return s || fallback;
}
function truncate(s, n) {
  const str = String(s ?? '');
  return str.length > n ? str.slice(0, n - 1).trimEnd() + '…' : str;
}
// Tamaño de fuente adaptativo para el título de portada según longitud.
function coverTitleSize(title) {
  const n = String(title || '').length;
  if (n <= 60) return 30;
  if (n <= 100) return 24;
  if (n <= 150) return 19;
  if (n <= 200) return 16;
  return 14;
}
// Extrae un nº de "40h", "40 horas", "20%" → 40 / 20
function toNumber(v) {
  if (v == null) return 0;
  const m = String(v).match(/(\d+(?:[.,]\d+)?)/);
  if (!m) return 0;
  return parseFloat(m[1].replace(',', '.')) || 0;
}

// ----- Selector: extrae y normaliza los datos del análisis -----
export function pickOnePagerData(bid, analysis) {
  const a = analysis || {};
  const snapshot = a.snapshot || {};
  const general = a.general || {};
  const service = a.service || {};
  const solvency = a.solvency || {};
  const team = a.team || {};
  const documents = a.documents || {};

  const fields = Array.isArray(general.fields) ? general.fields : [];
  const findField = (re) => {
    const f = fields.find((x) => re.test(String(x?.label || '')));
    return f?.value;
  };
  const budget = findField(/presupuesto|importe|valor estimado/i) || bid?.budget_base || bid?.budgetBase;
  const duration = findField(/plazo|duraci[oó]n/i);
  const deadline = findField(/fecha l[ií]mite|presentaci[oó]n|ofertas/i) || bid?.deadline;
  const rec = snapshot.recommendation || null;

  return {
    header: {
      title: or(bid?.title, 'Licitación'),
      entity: or(bid?.entity, ''),
      code: or(bid?.code || bid?.expediente, ''),
      region: or(bid?.region, ''),
      recommendation: rec,
      recommendationLabel: rec ? (REC_META[rec]?.label || rec) : null,
      recommendationRationale: safe(snapshot.rationale),
      confidence: snapshot.confidence,
    },
    kpis: [
      { label: 'Presupuesto', value: safe(budget), sub: 'base' },
      { label: 'Duración', value: safe(duration), sub: 'ejecución' },
      { label: 'Fecha límite', value: safe(deadline), sub: 'presentación' },
    ],
    general: fields.length ? fields : null,
    service: {
      objectText: safe(general.contractImplication || service.workload || bid?.description),
      activities: Array.isArray(service.activities) ? service.activities : [],
      hiddenLoad: safe(service.hiddenLoad),
    },
    solvencyTechnical: Array.isArray(solvency.technical) ? solvency.technical : [],
    solvencyProfessional: Array.isArray(solvency.professional) ? solvency.professional : [],
    team: {
      summary: safe(team.summary),
      profiles: Array.isArray(team.profiles) ? team.profiles : [],
      coverage: Array.isArray(team.coverage) ? team.coverage : [],
    },
    memory: {
      sections: Array.isArray(documents.envelope2) ? documents.envelope2 : [],
      tips: Array.isArray(a.criteria?.subjective)
        ? a.criteria.subjective.map((c) => ({ name: c.name, points: c.points, howToScore: c.howToScore, recommendation: c.recommendation }))
        : [],
    },
    risks: {
      items: Array.isArray(a.risks?.items) ? a.risks.items : [],
      maxExposure: safe(a.risks?.maxExposure),
    },
  };
}


// ----- Piezas visuales HTML -----
function kpiCards(kpis) {
  const w = Math.floor(100 / Math.max(1, kpis.length));
  return `<table class="kpi-grid"><tr>${kpis.map((k) => {
    const empty = !k.value;
    const val = empty ? '—' : String(k.value);
    const n = val.length;
    const sizePt = n <= 18 ? 14 : n <= 28 ? 12 : n <= 44 ? 10.5 : 9;
    return `<td class="kpi ${empty ? 'kpi-empty' : ''}" style="width:${w}%">
      <div class="kpi-label">${esc(k.label)}</div>
      <div class="kpi-value" style="font-size:${sizePt}pt">${esc(val)}</div>
      ${k.sub ? `<div class="kpi-sub">${esc(k.sub)}</div>` : ''}
    </td>`;
  }).join('')}</tr></table>`;
}

function recChip(rec) {
  if (!rec) return '';
  const m = REC_META[rec.recommendation] || null;
  if (!m) return '';
  return `<span class="rec-chip" style="color:${m.color};background:${m.bg};border-color:${m.color}">
    ● ${esc(m.label)}${rec.confidence != null ? ` · ${esc(rec.confidence)}%` : ''}
  </span>`;
}

function twoColTable(rows) {
  if (!rows?.length) return '';
  return `<table class="t-info"><colgroup><col style="width:36%"><col></colgroup><tbody>
    ${rows.map((r) => `<tr><th>${esc(r.label)}</th><td>${esc(r.value ?? NA)}</td></tr>`).join('')}
  </tbody></table>`;
}

function activitiesTable(items) {
  if (!items?.length) return `<div class="callout callout-info">Actividades no detalladas en el análisis.</div>`;
  const descOf = (x) => safe(x.description || x.summary || x.detail);
  const hasDesc = items.some((x) => descOf(x));
  // Anchos dinámicos: si hay descripción, cede espacio de Actividad y Entregables.
  const wAct = hasDesc ? '22%' : '32%';
  const wCat = hasDesc ? '15%' : '18%';
  const wHrs = hasDesc ? '11%' : '12%';
  return `<table class="t-data">
    <thead><tr>
      <th style="width:${wAct}">Actividad</th>
      ${hasDesc ? `<th style="width:30%">Descripción</th>` : ''}
      <th style="width:${wCat}">Categoría</th>
      <th style="width:${wHrs}">Horas</th>
      <th>Entregables</th>
    </tr></thead>
    <tbody>${items.map((x) => `<tr>
        <td><b>${esc(x.name || '—')}</b></td>
        ${hasDesc ? `<td>${esc(descOf(x) || '—')}</td>` : ''}
        <td>${esc(x.category || '—')}</td>
        <td>${esc(x.hours || '—')}</td>
        <td>${esc(x.deliverables || '—')}</td>
      </tr>`).join('')}</tbody>
  </table>`;
}

function checkList(items, columns = 1) {
  if (!items?.length) return `<div class="callout callout-info">No se han identificado requisitos específicos.</div>`;
  if (columns === 2) {
    const mid = Math.ceil(items.length / 2);
    const col = (arr) => `<ul class="checklist">${arr.map((x) => `<li><span class="check">✓</span><span>${esc(x)}</span></li>`).join('')}</ul>`;
    return `<table class="two-col"><tr>
      <td style="width:50%; padding-right:8px; vertical-align:top">${col(items.slice(0, mid))}</td>
      <td style="width:50%; padding-left:8px; vertical-align:top">${col(items.slice(mid))}</td>
    </tr></table>`;
  }
  return `<ul class="checklist">${items.map((x) => `<li><span class="check">✓</span><span>${esc(x)}</span></li>`).join('')}</ul>`;
}

function profileTable(profiles) {
  if (!profiles?.length) return `<div class="callout callout-info">Perfiles del equipo no detallados en el análisis.</div>`;
  const anyDedication = profiles.some((p) => safe(p.dedication));
  const anyCount = profiles.some((p) => safe(p.count));
  const anyRate = profiles.some((p) => safe(p.maxRate));
  const anyType = profiles.some((p) => p.essential === true || /esencial|valorable/i.test(String(p.type || '')));

  const rows = profiles.map((p, i) => {
    const num = String(i + 1);
    const functions = safe(p.functions || p.responsibilities || p.tasks);
    const titulacion = safe(p.education || p.degree || p.qualification || p.titulacion);
    const experiencia = safe(p.experience || p.yearsExperience || p.experiencia);
    const reqLines = [];
    if (titulacion) reqLines.push(`<b>Titulación:</b> ${esc(titulacion)}`);
    if (experiencia) reqLines.push(`<b>Experiencia:</b> ${esc(experiencia)}`);
    if (!reqLines.length && safe(p.requirement)) reqLines.push(esc(p.requirement));
    const reqHtml = reqLines.length ? reqLines.join('<br>') : '—';
    const tipoBadge = (() => {
      if (p.essential === true || /esencial/i.test(String(p.type || ''))) return `<span class="type-badge type-ess">Esencial</span>`;
      if (/valorable/i.test(String(p.type || ''))) return `<span class="type-badge type-val">Valorable</span>`;
      return '';
    })();
    return `<tr>
      <td class="col-num">${num}</td>
      <td><b>${esc(p.role || 'Perfil')}</b></td>
      <td>${functions ? esc(functions) : '—'}</td>
      <td class="col-req">${reqHtml}</td>
      ${anyCount ? `<td>${esc(safe(p.count) || '—')}</td>` : ''}
      ${anyDedication ? `<td>${esc(safe(p.dedication) || '—')}</td>` : ''}
      ${anyRate ? `<td>${esc(safe(p.maxRate) || '—')}</td>` : ''}
      ${anyType ? `<td>${tipoBadge || '—'}</td>` : ''}
    </tr>`;
  }).join('');

  return `<table class="t-data t-team">
    <thead><tr>
      <th style="width:3%">#</th>
      <th style="width:16%">Perfil</th>
      <th style="width:26%">Funciones</th>
      <th>Requisitos</th>
      ${anyCount ? `<th style="width:5%">Nº</th>` : ''}
      ${anyDedication ? `<th style="width:11%">Dedicación</th>` : ''}
      ${anyRate ? `<th style="width:10%">Tarifa máx.</th>` : ''}
      ${anyType ? `<th style="width:9%">Tipo</th>` : ''}
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

const DEFAULT_MEMORY_SECTIONS = [
  'Metodología de trabajo y enfoque técnico',
  'Plan de trabajo y cronograma',
  'Equipo humano adscrito al servicio',
  'Mejoras y valor añadido',
  'Sistema de calidad, seguimiento e indicadores',
];

function stripLeadingNumber(str) {
  let s = String(str || '').trim();
  // Elimina prefijos tipo "1.", "1)", "1 -", "01.", "1.1", "I.", "IV -" repetidos.
  s = s.replace(/^(?:\d+(?:\.\d+)*[.)\-–]?\s+)+/, '');
  s = s.replace(/^[IVX]+[.)\-–]?\s+/i, '');
  return s.trim();
}

const FORMAT_RE = /(tipograf|fuente|arial|times|calibri|verdana|helvetica|interlineado|espaciado|tama[nñ]o de (?:letra|fuente)|cuerpo\s*\d+|\d+\s*(?:pt|puntos)|m[aá]rgen|formato|extensi[oó]n|n[ºo°]?\s*(?:m[aá]ximo\s*de\s*)?p[aá]ginas|páginas m[aá]x|maquetaci[oó]n|encuadernaci[oó]n|portada|pdf|word|docx)/i;

function memorySections(sections) {
  const rawSecs = (sections?.length ? sections : DEFAULT_MEMORY_SECTIONS).map((s) => stripLeadingNumber(s));
  const isDefault = !sections?.length;
  // Separar apartados de "base formal" (tipografía, tamaño, extensión…) del contenido temático numerado.
  const formatItems = rawSecs.filter((s) => FORMAT_RE.test(s));
  const contentItems = rawSecs.filter((s) => !FORMAT_RE.test(s));

  const formatBlock = `
    <div class="mem-format">
      <div class="mem-format-hd">Base formal de la memoria</div>
      <div class="mem-format-sub">Tipografía, tamaño de letra, interlineado, márgenes, extensión máxima y formato de entrega exigidos por el pliego. <b>Aplican a todos los apartados</b> y son motivo de exclusión si no se cumplen.</div>
      ${formatItems.length
        ? `<ul class="mem-format-list">${formatItems.map((s) => `<li>${esc(s)}</li>`).join('')}</ul>`
        : `<div class="mem-format-empty">Consultar en el pliego los requisitos concretos de tipografía, tamaño, márgenes y extensión máxima.</div>`}
    </div>`;

  return `
    ${isDefault ? `<div class="callout callout-ai"><b>◆ Inferencia de Lizzi (IA)</b> — el pliego no detalla apartados explícitos; esta estructura es una propuesta habitual, no un contenido del expediente.</div>` : ''}
    ${formatBlock}
    <div class="mem-content-hd">Apartados de contenido</div>
    <ul class="mem-list">${contentItems.map((s, i) => `<li><span class="mem-num">${i + 1}</span><span class="mem-txt">${esc(s)}</span></li>`).join('')}</ul>`;
}

function memoryTips(tips) {
  if (!tips?.length) return '';
  const totalPts = tips.reduce((a, t) => a + (toNumber(t.points) || 0), 0) || 0;
  return `
    <div class="mem-tips">
      <div class="mem-tips-hd">
        <span>★ Cómo puntuar más en la memoria</span>
        ${totalPts > 0 ? `<span class="mem-tips-total">${totalPts} pts en juego</span>` : ''}
      </div>
      <ul class="mem-tips-list">${tips.map((t) => `<li><b>${esc(t.name || 'Criterio')}</b>${t.points ? ` <span class="tip-pts">${esc(t.points)} pts</span>` : ''}${t.recommendation ? `<div class="tip-rec">${esc(t.recommendation)}</div>` : t.howToScore ? `<div class="tip-rec">${esc(t.howToScore)}</div>` : ''}</li>`).join('')}</ul>
    </div>`;
}

// Badge de origen del contenido: literal del pliego vs. análisis generado por Lizzi.
function srcBadge(source) {
  return source === 'ai'
    ? '<span class="src-badge src-badge-ai">◆ Análisis Lizzi (IA)</span>'
    : '<span class="src-badge src-badge-doc">● Extraído del pliego</span>';
}

function risksTable(items) {
  if (!items?.length) return '';
  return `<table class="t-data"><thead><tr>
      <th style="width:34%">Riesgo</th><th style="width:11%">Prob.</th><th style="width:11%">Impacto</th><th style="width:14%">Importe</th><th>Mitigación propuesta</th>
    </tr></thead><tbody>${items.map((r) => `<tr>
      <td>${esc(or(r.risk, NA))}</td>
      <td>${esc(or(r.probability, NA))}</td>
      <td>${esc(or(r.impact, NA))}</td>
      <td>${esc(or(r.impactValue, NA))}</td>
      <td>${esc(or(r.mitigation, NA))}</td>
    </tr>`).join('')}</tbody></table>`;
}

function coverageTable(items) {
  if (!items?.length) return '';
  return `<table class="t-data"><thead><tr>
      <th style="width:28%">Perfil</th><th style="width:16%">Asignado</th><th style="width:16%">Necesario</th><th style="width:14%">Cobertura</th><th>Riesgo</th>
    </tr></thead><tbody>${items.map((c) => `<tr>
      <td>${esc(or(c.profile, NA))}</td>
      <td>${esc(or(c.assigned, NA))}</td>
      <td>${esc(or(c.needed, NA))}</td>
      <td>${esc(or(c.coverage, NA))}</td>
      <td>${esc(or(c.risk, NA))}</td>
    </tr>`).join('')}</tbody></table>`;
}


function sectionDivider(label) {
  return `<div class="sec-divider"><span>${esc(label)}</span></div>`;
}

// ----- Estilos del documento (Word y PDF compartidos) -----
function buildCss(coverTitlePt) {
  return `
  @page { size: A4; margin: 16mm 15mm 18mm 15mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', 'Calibri', 'Helvetica Neue', Arial, sans-serif; color:#111827; font-size:10pt; line-height:1.5; margin:0; }
  .page { position: relative; margin-bottom: 10mm; }
  .page + .page { padding-top: 4mm; border-top: 1px solid #e5e7eb; }
  .page:last-child { margin-bottom: 0; }
  p { margin: 6px 0 10px; orphans: 3; widows: 3; }

  /* Cabecera compacta de páginas internas */
  .doc-header { border-bottom: 2px solid #0f766e; padding-bottom: 8px; margin-bottom: 14px; display:table; width:100%; table-layout: fixed; }
  .doc-header .dh-l { display:table-cell; vertical-align:middle; width:70%; padding-right:12px; }
  .doc-header .dh-r { display:table-cell; vertical-align:middle; text-align:right; color:#6b7280; font-size:9pt; width:30%; }
  .doc-eyebrow { font-size:8.5pt; letter-spacing:1.5px; text-transform:uppercase; color:#0f766e; font-weight:700; }
  .doc-subtitle { font-size:10pt; color:#374151; margin-top:2px; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

  h1.section-title { font-size:15pt; color:#0b3d38; margin:0 0 4px; font-weight:800; display:inline-block; }
  h1.section-title:after { content:''; display:block; width:44px; height:3px; background:#0f766e; margin-top:6px; border-radius:2px; }
  .section-head { display:table; width:100%; margin-bottom:6px; }
  .section-head .sh-l { display:table-cell; vertical-align:bottom; }
  .section-head .sh-r { display:table-cell; vertical-align:bottom; text-align:right; }
  .section-count { display:inline-block; padding:3px 9px; background:#0f766e; color:#fff; border-radius:999px; font-size:8.5pt; font-weight:700; margin-left:6px; }
  .section-intro { color:#6b7280; margin: 6px 0 12px; font-size:10pt; font-style: italic; }

  /* Divisor entre bloques fusionados */
  .sec-divider { margin: 18px 0 10px; border-top: 1px solid #d1fae5; text-align:center; }
  .sec-divider span { display:inline-block; transform: translateY(-9px); background:#fff; padding: 0 12px; font-size:8pt; letter-spacing:2px; text-transform:uppercase; color:#0f766e; font-weight:700; }

  /* Portada */
  .cover { padding: 20mm 0 0; position:relative; }
  .cover-bar { position:absolute; top:0; left:-15mm; width:8mm; height:100%; background:#0f766e; }
  .cover-brand { font-size:9pt; letter-spacing:2.5px; text-transform:uppercase; color:#0f766e; font-weight:800; }
  .cover-title { font-size: ${coverTitlePt}pt; font-weight:800; color:#0b3d38; margin: 10px 0 14px; line-height:1.15; word-break: break-word; hyphens: auto; overflow-wrap: anywhere; }
  .cover-entity { font-size:12.5pt; color:#374151; margin-bottom: 4px; font-weight:600; }
  .cover-meta { color:#6b7280; font-size:10pt; margin-bottom: 18px; }

  /* KPIs premium en portada — tarjetas grandes con icono, label y valor */
  .cover-kpis { width:100%; border-collapse: separate; border-spacing: 10px 0; margin: 10px 0 6px; }
  .cover-kpi { vertical-align: top; padding: 14px 16px; background: linear-gradient(160deg, #ffffff 0%, #f0fdfa 100%); border: 1px solid #99f6e4; border-top: 3px solid #0f766e; border-radius: 10px; page-break-inside: avoid; }
  .cover-kpi-ic { display:inline-block; width:22px; height:22px; line-height:22px; text-align:center; background:#0f766e; color:#fff; border-radius:6px; font-size:11pt; font-weight:800; margin-bottom:8px; }
  .cover-kpi-lb { font-size:8pt; letter-spacing:1.4px; text-transform:uppercase; color:#0f766e; font-weight:800; margin-bottom:4px; }
  .cover-kpi-val { font-size:13pt; font-weight:800; color:#0b3d38; line-height:1.25; word-break: break-word; overflow-wrap: anywhere; }
  .cover-kpi-sub { font-size:8.5pt; color:#6b7280; margin-top:4px; }
  .cover-kpi.empty { background:#f9fafb; border-color:#e5e7eb; border-top-color:#d1d5db; }
  .cover-kpi.empty .cover-kpi-ic { background:#9ca3af; }
  .cover-kpi.empty .cover-kpi-lb { color:#9ca3af; }
  .cover-kpi.empty .cover-kpi-val { color:#9ca3af; }

  .cover-rec { margin-top: 16mm; padding: 16px 20px; background: #f0fdfa; border: 1px solid #99f6e4; border-left: 6px solid #0f766e; border-radius: 8px; page-break-inside: avoid; }
  .cover-rec-lb { font-size:8.5pt; letter-spacing:1.5px; text-transform:uppercase; color:#0f766e; font-weight:700; margin-bottom:6px; }
  .cover-rec-val { font-size:17pt; font-weight:800; color:#0b3d38; margin-bottom: 6px; }
  .cover-rec-conf { font-size:9.5pt; color:#0f766e; font-weight:600; margin-bottom:6px; }
  .cover-rec-rat { color:#374151; font-size:10pt; }

  .cover-foot { margin-top: 14mm; color:#9ca3af; font-size:9pt; border-top: 1px solid #e5e7eb; padding-top: 8px; display:table; width:100%; }
  .cover-foot .cf-l { display:table-cell; }
  .cover-foot .cf-r { display:table-cell; text-align:right; font-weight:700; letter-spacing:3px; color:#e5e7eb; font-size:14pt; }

  /* KPIs */
  .kpi-grid { width:100%; border-collapse: separate; border-spacing: 6px 0; margin: 4px 0 14px; }
  .kpi { padding: 10px 12px; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 8px; vertical-align: top; page-break-inside: avoid; }
  .kpi-empty { background:#f9fafb; border-color:#e5e7eb; }
  .kpi-empty .kpi-label, .kpi-empty .kpi-value { color:#9ca3af; }
  .kpi-label { font-size:8pt; letter-spacing:1px; text-transform:uppercase; color:#0f766e; font-weight:700; }
  .kpi-value { font-size:14pt; font-weight:800; color:#0b3d38; margin: 4px 0 2px; line-height:1.2; word-break: break-word; overflow-wrap: anywhere; }
  .kpi-sub { font-size:8.5pt; color:#6b7280; }

  /* Rec chip */
  .rec-chip { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size:9pt; font-weight:700; border:1px solid; }

  /* Tablas */
  table { width:100%; border-collapse: collapse; margin: 6px 0 12px; font-size:9.5pt; }
  .t-info th, .t-info td { border: 1px solid #e5e7eb; padding: 7px 10px; text-align: left; vertical-align: top; }
  .t-info th { background: #f9fafb; color:#0b3d38; font-weight:700; }
  .t-info tbody tr:nth-child(even) td, .t-info tbody tr:nth-child(even) th { background:#fbfcfd; }
  .t-info tbody tr { page-break-inside: avoid; }
  .t-data th, .t-data td { border: 1px solid #e5e7eb; padding: 7px 10px; text-align: left; vertical-align: top; }
  .t-data thead th { background: #0f766e; color: #ffffff; font-weight:700; font-size:9pt; letter-spacing:.3px; }
  .t-data tbody tr:nth-child(even) td { background: #f9fafb; }
  .t-data tbody tr { page-break-inside: avoid; }

  /* Callouts */
  .callout { padding: 10px 14px; border-radius: 8px; margin: 8px 0 12px; font-size:9.5pt; border-left: 4px solid; }
  .callout b { color: inherit; }
  .callout-warn { background: #fffbeb; border-color: #f59e0b; color:#78350f; }
  .callout-info { background: #eff6ff; border-color: #3b82f6; color:#1e3a8a; }
  .callout-ok { background: #ecfdf5; border-color: #10b981; color:#064e3b; }
  .callout-ai { background: #f5f3ff; border-color: #7c3aed; color:#4c1d95; }

  /* Origen del contenido: pliego vs. IA */
  .src-badge { display:inline-block; padding: 3px 10px; border-radius: 999px; font-size:8pt; font-weight:800; letter-spacing:.3px; white-space:nowrap; }
  .src-badge-doc { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .src-badge-ai { background:#f5f3ff; color:#5b21b6; border:1px solid #ddd6fe; }
  .dh-src { display:block; margin-top:3px; font-size:8pt; font-weight:700; letter-spacing:.3px; }
  .dh-src-doc { color:#0f766e; }
  .dh-src-ai { color:#7c3aed; }
  .page-ai .doc-header { border-bottom-color:#7c3aed; }
  .page-ai .section-title { color:#4c1d95; }
  .ai-page-warn { background:#f5f3ff; border:1px solid #ddd6fe; border-left:6px solid #7c3aed; border-radius:8px; padding: 12px 16px; margin: 4px 0 14px; page-break-inside: avoid; }
  .ai-warn-hd { color:#5b21b6; font-weight:800; font-size:11pt; letter-spacing:.3px; margin-bottom:4px; }
  .ai-warn-tx { color:#4b5563; font-size:9.5pt; }
  .ai-sub-hd { color:#4c1d95; font-weight:800; font-size:11pt; margin: 14px 0 4px; letter-spacing:.3px; }
  .ai-sub-sub { color:#6b7280; font-size:9pt; margin-bottom:6px; }
  .page-ai .t-data thead th { background:#5b21b6; }

  .callout-warn b { color: #78350f; }
  .callout-hd { font-weight:800; margin-bottom:4px; display:block; }
  .para-long { text-align: justify; }

  /* Checklist */
  .checklist { list-style: none; padding: 0; margin: 6px 0 12px; }
  .checklist li { display: table; width:100%; padding: 6px 8px; border-bottom: 1px dashed #e5e7eb; }
  .checklist li > span { display: table-cell; vertical-align: top; }
  .checklist .check { color:#0f766e; font-weight:800; width:22px; text-align:center; }
  .two-col td { vertical-align:top; }

  /* Tabla de equipo */
  .t-team td { font-size:9.5pt; line-height:1.45; }
  .t-team .col-num { text-align:center; font-weight:800; color:#0f766e; }
  .t-team .col-req b { color:#0b3d38; }
  .type-badge { display:inline-block; padding: 2px 8px; border-radius: 999px; font-size:8pt; font-weight:700; }
  .type-ess { background:#fef3c7; color:#78350f; }
  .type-val { background:#e0f2fe; color:#075985; }

  /* Memoria */
  .mem-list { list-style: none; padding: 0; margin: 6px 0 10px; }
  .mem-list li { margin-bottom: 8px; page-break-inside: avoid; }
  .mem-num { float:left; width:26px; height:24px; line-height:24px; text-align:center; background:#0f766e; color:#fff; border-radius:6px; font-size:10pt; font-weight:800; margin-right:10px; }
  .mem-txt { display:block; overflow:hidden; padding-top:3px; color:#0b3d38; font-weight:600; font-size:10.5pt; }
  .mem-tips { margin-top: 12px; padding: 12px 16px; background:#fefce8; border: 1px solid #fde68a; border-radius: 8px; }
  .mem-tips-hd { color:#78350f; font-weight:800; margin-bottom:8px; letter-spacing:.3px; display:table; width:100%; }
  .mem-tips-hd > span { display: table-cell; vertical-align:middle; }
  .mem-tips-hd > span:last-child { text-align:right; background:#78350f; color:#fff; padding: 3px 10px; border-radius: 999px; font-size:8.5pt; letter-spacing:0; }
  .mem-tips-list { list-style: none; padding-left: 0; margin: 0; }
  .mem-tips-list li { margin-bottom: 8px; font-size:9pt; color:#374151; page-break-inside: avoid; padding-left: 14px; position: relative; }
  .mem-tips-list li:before { content: "–"; position: absolute; left: 0; color:#78350f; font-weight:800; }
  .tip-pts { display:inline-block; margin-left:6px; padding:1px 6px; background:#0f766e; color:#fff; border-radius:4px; font-size:8pt; }
  .tip-rec { color:#4b5563; font-size:8.5pt; margin-top:2px; }

  /* Memoria — base formal + apartados */
  .mem-format { margin: 4px 0 14px; padding: 12px 14px; background:#eef2ff; border:1px solid #c7d2fe; border-left:5px solid #4338ca; border-radius:8px; page-break-inside: avoid; }
  .mem-format-hd { color:#3730a3; font-weight:800; font-size:10.5pt; letter-spacing:.3px; margin-bottom:4px; }
  .mem-format-sub { color:#4b5563; font-size:9pt; margin-bottom:6px; }
  .mem-format-list { list-style: disc; padding-left: 20px; margin: 4px 0 0; }
  .mem-format-list li { color:#1e1b4b; font-size:9.5pt; margin-bottom:3px; }
  .mem-format-empty { color:#4b5563; font-size:9pt; font-style: italic; }
  .mem-content-hd { color:#0b3d38; font-weight:800; font-size:10.5pt; margin: 6px 0 6px; letter-spacing:.3px; }

  .foot-brand { margin-top: 14px; color:#9ca3af; font-size:8pt; text-align:center; border-top: 1px solid #f3f4f6; padding-top:6px; }

  /* Franja de marca compacta (nueva página 1) */
  .brand-strip { border-left: 4px solid #0f766e; padding: 6px 0 6px 12px; margin: 4px 0 12px; }
  .bs-eyebrow { font-size:8.5pt; letter-spacing:2px; text-transform:uppercase; color:#0f766e; font-weight:800; }
  .bs-title { font-weight:800; color:#0b3d38; line-height:1.15; margin: 4px 0 6px; word-break: break-word; overflow-wrap: anywhere; }
  .bs-meta { color:#6b7280; font-size:9.5pt; }

  /* Tarjeta de recomendación (nueva ubicación arriba de la página 1) */
  .rec-card { padding: 12px 16px; background:#f0fdfa; border:1px solid #99f6e4; border-left:6px solid #0f766e; border-radius:8px; margin: 6px 0 14px; page-break-inside: avoid; }
  .rec-card-hd { display:table; width:100%; margin-bottom:6px; }
  .rec-card-hd-l { display:table-cell; vertical-align:middle; }
  .rec-card-conf { display:table-cell; vertical-align:middle; text-align:center; width:120px; color:#fff; font-weight:700; padding: 6px 10px; border-radius:999px; font-size:9pt; }
  .rec-card-lb { font-size:8.5pt; letter-spacing:1.5px; text-transform:uppercase; font-weight:700; margin-bottom:2px; }
  .rec-card-val { font-size:15pt; font-weight:800; color:#0b3d38; line-height:1.2; }
  .rec-card-rat { color:#374151; font-size:10pt; }

  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;
}

// ----- Construcción del HTML del one-pager -----
export function buildOnePagerHtml(bid, analysis) {
  const d = pickOnePagerData(bid, analysis);
  const today = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  const coverPt = coverTitleSize(d.header.title);
  const CSS = buildCss(coverPt);

  const shortTitle = truncate(d.header.title, 80); // <title> del <head>
  const headerTitle = truncate(d.header.title, 90); // solo cabeceras internas

  const mkHeader = (src = 'doc') => `
    <div class="doc-header">
      <div class="dh-l">
        <div class="doc-eyebrow">One-Pager · Análisis Lizzi</div>
        <div class="doc-subtitle" title="${esc(d.header.title)}"><b>${esc(headerTitle)}</b></div>
      </div>
      <div class="dh-r">
        ${d.header.entity ? `${esc(truncate(d.header.entity, 40))}<br>` : ''}
        ${d.header.code ? `<b>${esc(d.header.code)}</b>` : ''}
        <span class="dh-src ${src === 'ai' ? 'dh-src-ai' : 'dh-src-doc'}">${src === 'ai' ? 'Interpretación generada por IA' : 'Contenido literal del pliego'}</span>
      </div>
    </div>`;
  const pageHeader = mkHeader('doc');
  const footDoc = '<div class="foot-brand">Lizzi · One-Pager de licitación · Datos extraídos del expediente</div>';

  // Barra de color según recomendación
  const barColor = d.header.recommendation ? (REC_META[d.header.recommendation]?.bar || '#0f766e') : '#0f766e';

  // KPIs premium en portada
  const kpiDefs = [
    { l: 'Presupuesto', v: d.kpis[0].value, s: 'base', icon: '€' },
    { l: 'Duración', v: d.kpis[1].value, s: 'ejecución', icon: '◷' },
    { l: 'Fecha límite', v: d.kpis[2].value, s: 'presentación', icon: '◆' },
  ];
  const coverKpis = `<table class="cover-kpis"><tr>${kpiDefs.map((k) => {
    const empty = !k.v;
    const val = empty ? '—' : String(k.v);
    const n = val.length;
    const sizePt = n <= 16 ? 14 : n <= 26 ? 12 : n <= 42 ? 10.5 : 9.5;
    return `<td class="cover-kpi ${empty ? 'empty' : ''}" style="width:33.33%">
      <div class="cover-kpi-ic">${k.icon}</div>
      <div class="cover-kpi-lb">${esc(k.l)}</div>
      <div class="cover-kpi-val" style="font-size:${sizePt}pt">${esc(val)}</div>
      ${k.s ? `<div class="cover-kpi-sub">${esc(k.s)}</div>` : ''}
    </td>`;
  }).join('')}</tr></table>`;

  // Página 1 — Marca compacta + KPIs + Información general (solo datos del pliego)
  const brandStrip = `
    <div class="brand-strip">
      <div class="bs-eyebrow">Lizzi · One-Pager</div>
      <div class="bs-title" style="font-size:18pt" title="${esc(d.header.title)}">${esc(truncate(d.header.title, 110))}</div>
      <div class="bs-meta">
        ${d.header.entity ? `<b>${esc(d.header.entity)}</b>` : ''}
        ${d.header.code ? ` · Expediente <b>${esc(d.header.code)}</b>` : ''}
        ${d.header.region ? ` · ${esc(d.header.region)}` : ''}
        ${d.kpis[2].value ? ` · Fecha límite <b>${esc(d.kpis[2].value)}</b>` : ''}
        · Generado el ${esc(today)}
      </div>
    </div>`;

  // Información general (sin duplicar KPIs en la tabla)
  const dupRe = /(presupuesto|importe|valor estimado|plazo|duraci[oó]n|fecha l[ií]mite|presentaci[oó]n|ofertas)/i;
  const generalRows = (d.general || []).filter((f) => !dupRe.test(String(f?.label || '')));
  const infoRows = generalRows.length ? generalRows.map((f) => ({ label: f.label, value: f.value })) : [
    { label: 'Título', value: d.header.title },
    { label: 'Entidad', value: d.header.entity },
    { label: 'Expediente', value: d.header.code },
    { label: 'Ámbito geográfico', value: d.header.region },
  ];
  const p1 = `
    <section class="page">
      ${pageHeader}
      ${brandStrip}
      ${coverKpis}
      <div class="section-head">
        <div class="sh-l"><h1 class="section-title">Información general</h1></div>
        <div class="sh-r">${srcBadge('doc')}</div>
      </div>
      <p class="section-intro">Datos clave del expediente y contexto contractual, tal y como constan en el pliego. La valoración y las recomendaciones de Lizzi están al final del documento.</p>
      ${twoColTable(infoRows)}
    </section>`;

  // Página 2 — Descripción del servicio
  const objText = d.service.objectText || '';
  const p2 = `
    <section class="page">
      <div class="section-head">
        <div class="sh-l"><h1 class="section-title">Descripción del servicio</h1></div>
        <div class="sh-r">${srcBadge('doc')}${d.service.activities?.length ? `<span class="section-count">${d.service.activities.length} actividades</span>` : ''}</div>
      </div>
      <p class="section-intro">Qué hay que ejecutar y con qué entregables, según el pliego.</p>
      ${objText ? `<p class="para-long">${esc(objText)}</p>` : ''}
      ${activitiesTable(d.service.activities)}
    </section>`;

  // Decidir fusión Solvencia + Equipo — los perfiles profesionales van SOLO en Equipo
  const solvCount = d.solvencyTechnical?.length || 0;
  const teamCount = d.team.profiles.length;
  const canMerge = solvCount <= 6 && teamCount <= 5 && (d.team.summary?.length || 0) < 400;
  const solvCols = solvCount > 6 ? 2 : 1;

  const solvencyBlock = `
    <div class="section-head">
      <div class="sh-l"><h1 class="section-title">Requisitos de solvencia técnica</h1></div>
      <div class="sh-r">${srcBadge('doc')}${solvCount ? `<span class="section-count">${solvCount} requisitos</span>` : ''}</div>
    </div>
    <p class="section-intro">Requisitos exigidos para poder concurrir (excluyendo solvencia económica). Los perfiles profesionales se detallan en el apartado de Equipo.</p>
    ${checkList(d.solvencyTechnical, solvCols)}`;

  const teamBlock = `
    <div class="section-head">
      <div class="sh-l"><h1 class="section-title">Equipo requerido</h1></div>
      <div class="sh-r">${srcBadge('doc')}${teamCount ? `<span class="section-count">${teamCount} perfiles</span>` : ''}</div>
    </div>
    <p class="section-intro">Perfiles mínimos y dedicación exigidos por el pliego.</p>
    ${d.team.summary ? `<p class="para-long">${esc(d.team.summary)}</p>` : ''}
    ${profileTable(d.team.profiles)}`;

  const mergedPage = `
    <section class="page">
      ${solvencyBlock}
      ${sectionDivider('Equipo')}
      ${teamBlock}
    </section>`;
  const pSolv = `<section class="page">${solvencyBlock}</section>`;
  const pTeam = `<section class="page">${teamBlock}</section>`;

  // Página de memoria técnica — exigencias del pliego
  const pMem = `
    <section class="page">
      <div class="section-head">
        <div class="sh-l"><h1 class="section-title">Memoria Técnica · exigencias del pliego</h1></div>
        <div class="sh-r">${srcBadge('doc')}</div>
      </div>
      <p class="section-intro">Base formal y apartados de contenido que el pliego exige para la memoria.</p>
      ${memorySections(d.memory.sections)}
      ${footDoc}
    </section>`;

  // Última página — TODO el análisis e insights generados por Lizzi (IA)
  const aiRec = d.header.recommendation ? `
    <div class="rec-card" style="border-left-color:${barColor}">
      <div class="rec-card-hd">
        <div class="rec-card-hd-l">
          <div class="rec-card-lb" style="color:${barColor}">Recomendación IIL · generada por Lizzi</div>
          <div class="rec-card-val">${esc(d.header.recommendationLabel)}</div>
        </div>
        ${d.header.confidence != null ? `<div class="rec-card-conf" style="background:${barColor}">Confianza ${esc(d.header.confidence)}%</div>` : ''}
      </div>
      ${d.header.recommendationRationale ? `<div class="rec-card-rat">${esc(d.header.recommendationRationale)}</div>` : ''}
    </div>` : '';

  const aiHidden = d.service.hiddenLoad ? `
    <div class="ai-sub-hd">Cargas ocultas detectadas</div>
    <div class="ai-sub-sub">Esfuerzo o coste no evidente en el precio, inferido por Lizzi a partir del expediente.</div>
    <div class="callout callout-warn"><span class="callout-hd">⚠ No evidente en el precio</span>${esc(d.service.hiddenLoad)}</div>` : '';

  const aiRisks = d.risks.items.length ? `
    <div class="ai-sub-hd">Matriz de riesgos</div>
    <div class="ai-sub-sub">Estimación de probabilidad, impacto y mitigación elaborada por Lizzi. No consta así en el pliego.</div>
    ${risksTable(d.risks.items)}
    ${d.risks.maxExposure ? `<p class="section-intro"><b>Exposición máxima estimada:</b> ${esc(d.risks.maxExposure)}</p>` : ''}` : '';

  const aiCoverage = d.team.coverage.length ? `
    <div class="ai-sub-hd">Cobertura del equipo</div>
    <div class="ai-sub-sub">Contraste entre lo exigido y la capacidad disponible, según el análisis de Lizzi.</div>
    ${coverageTable(d.team.coverage)}` : '';

  const tipsHtml = memoryTips(d.memory.tips);
  const aiTips = tipsHtml ? `
    <div class="ai-sub-hd">Cómo puntuar más en la memoria</div>
    <div class="ai-sub-sub">Recomendaciones de redacción propuestas por Lizzi sobre los criterios subjetivos del pliego.</div>
    ${tipsHtml}` : '';

  const aiBody = `${aiRec}${aiHidden}${aiRisks}${aiCoverage}${aiTips}`;
  const pAi = `
    <section class="page page-ai">
      ${mkHeader('ai')}
      <div class="section-head">
        <div class="sh-l"><h1 class="section-title">Análisis Lizzi (IA)</h1></div>
        <div class="sh-r">${srcBadge('ai')}</div>
      </div>
      <div class="ai-page-warn">
        <div class="ai-warn-hd">◆ Este apartado NO forma parte del pliego</div>
        <div class="ai-warn-tx">Son conclusiones, estimaciones y recomendaciones generadas por Lizzi (IA) a partir del expediente. Verificar contra los documentos oficiales antes de usarlas como base de decisión.</div>
      </div>
      ${aiBody || '<p class="section-intro">No hay análisis adicional disponible para esta licitación.</p>'}
      <div class="foot-brand">Lizzi · Contenido generado por IA · ${esc(today)}</div>
    </section>`;

  const middle = canMerge ? mergedPage : (pSolv + pTeam);
  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>One-Pager · ${esc(shortTitle)}</title><style>${CSS}</style></head><body>${p1}${p2}${middle}${pMem}${pAi}</body></html>`;

}

// ----- Descargas -----
function slugify(str, max = 40) {
  return String(str || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .join('_')
    .slice(0, max)
    .replace(/_+$/, '');
}

function fileBase(bid) {
  const entity = slugify(bid?.entity || bid?.organism || bid?.client, 30);
  const code = slugify(bid?.code || bid?.expediente || bid?.reference, 20);
  const titleShort = slugify((bid?.title || '').split(/[·:\-–—|(]/)[0], 30);
  const parts = [entity, code || titleShort].filter(Boolean);
  const base = parts.join('_') || slugify(bid?.title, 40) || 'licitacion';
  const date = new Date().toISOString().slice(0, 10);
  return `${base}_${date}`;
}

export function downloadOnePagerWord(bid, analysis) {
  const html = buildOnePagerHtml(bid, analysis);
  const wordHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">${html.replace(/^<!DOCTYPE html>|<html[^>]*>/, '').replace(/<\/html>$/, '')}</html>`;
  const blob = new Blob(['\ufeff', wordHtml], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `OnePager_${fileBase(bid)}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadOnePagerPdf(bid, analysis) {
  const html = buildOnePagerHtml(bid, analysis);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  const done = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) { /* noop */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) { /* noop */ } }, 1500);
  };
  if (iframe.contentWindow.document.readyState === 'complete') {
    setTimeout(done, 250);
  } else {
    iframe.onload = () => setTimeout(done, 250);
  }
}
